import { BasicRectangleButton } from '@klokgroep/shared-components/src/Button/BasicRectangleButton';
import { ChangeEventHandler, useCallback, useMemo, useState } from 'react';
import { FilterIcon } from '@klokgroep/shared-components/src/Icons';
import { FilterSection } from '@klokgroep/shared-components/src/Filters';
import { Input } from '@klokgroep/shared-components/src/Input';
import { Modal } from '@klokgroep/shared-components/src/Modal';
import { useCommonTranslations } from '@klokgroep/shared-components/utils/useTranslations';
import { useRouter } from 'next/router';
import styles from './ProjectsFilters.module.css';

interface Properties {
  filterProperties: {
    [key: string]: {
      key: string;
      onChange: (value: string) => void;
      options: any[];
      selected: string[] | undefined;
      title: string;
    };
  };
  onSearchChange: ChangeEventHandler<HTMLInputElement>;
  searchValue?: string;
}

export const ProjectsFilters = ({ filterProperties, onSearchChange, searchValue = '' }: Properties) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);
  const t = useCommonTranslations();

  const onModalOpen = useCallback(() => setShowModal(true), []);
  const onModalClose = useCallback(() => setShowModal(false), []);

  const activeFilterAmount = useMemo(
    () => Object.values(filterProperties).flatMap(({ selected }) => selected).length,
    [filterProperties]
  );

  const onReset = useCallback(() => {
    const keysWithUndefined = Object.fromEntries(Object.values(filterProperties).map(({ key }) => [key, undefined]));
    const query = { ...router.query, ...keysWithUndefined, page: 1 };
    router.replace({ query }, { pathname: router.asPath.split('?')[0], query }, { shallow: true });
    onModalClose();
  }, [filterProperties, onModalClose, router]);

  return (
    <div className={styles.container}>
      <div className={styles.statusAndButtonsContainer}>
        <div className={styles.statusContainer}>
          <FilterSection
            horizontal
            items={filterProperties.status.options}
            onChange={filterProperties.status.onChange}
            title={filterProperties.status.title}
            titleAs="h4"
          />
        </div>
        <div className={styles.buttonsContainer}>
          <BasicRectangleButton Icon={FilterIcon} onClick={onModalOpen}>
            {t('projects_overview_filters')}
            {activeFilterAmount > 0 ? ` (${activeFilterAmount})` : ``}
          </BasicRectangleButton>
        </div>
      </div>

      {showModal ? (
        <Modal smallPadding title={t('filters_plural')} showLogo={false} onClose={onModalClose}>
          <div className={styles.filtersContainer}>
            <Input
              value={searchValue}
              placeholder={t('projects_overview_filters_search_placeholder')}
              onChange={onSearchChange}
            />

            {Object.values(filterProperties).map(({ key, title, options, onChange }) => (
              <FilterSection collapsible titleAs="h4" key={key} title={title} items={options} onChange={onChange} />
            ))}
          </div>

          <div className={styles.filterButtonsContainer}>
            <BasicRectangleButton onClick={onModalClose}>{t('apply_filter')}</BasicRectangleButton>
            <BasicRectangleButton onClick={onReset}>{t('reset_filters')}</BasicRectangleButton>
          </div>
        </Modal>
      ) : undefined}
    </div>
  );
};
